// The steps of each generation pipeline, in the order the user sees them.
//
// The backend is the source of truth for the use case list
// (use_case/progress_steps.py); this is its mirror, used to render the popup
// before the first event arrives and to order tasks that carry no position.
// Events carry their position (step_index) and that wins when present, so a
// backend reorder shows correctly without a frontend release.

export type Pipeline = 'demand_letter' | 'deposition_summary' | 'use_case';

export interface TaskDefinition {
  task_type: string;
  task_name: string;
  weight: number;
}

export const TASK_DEFINITIONS_BY_PIPELINE: Record<
  Pipeline,
  readonly TaskDefinition[]
> = {
  demand_letter: [
    {
      task_type: 'Generate Vector Store',
      task_name: 'Analyzing Documents',
      weight: 30,
    },
    {
      task_type: 'OCR Text Extraction',
      task_name: 'Extracting Text',
      weight: 15,
    },
    {
      task_type: 'ICD Code Extraction',
      task_name: 'Identifying Medical Codes',
      weight: 10,
    },
    {
      task_type: 'Liability Mistakes',
      task_name: 'Analyzing Liability Issues',
      weight: 10,
    },
    {
      task_type: 'Document Extraction',
      task_name: 'Processing Documents',
      weight: 10,
    },
    {
      task_type: 'Sections Generation',
      task_name: 'Generating Sections',
      weight: 20,
    },
    {
      task_type: 'Global Generation',
      task_name: 'Finalizing Document',
      weight: 5,
    },
  ],
  deposition_summary: [
    {
      task_type: 'DEPOSITION_VECTOR_STORE',
      task_name: 'Preparing Document Index',
      weight: 35,
    },
    {
      task_type: 'DEPOSITION_EXHIBITS',
      task_name: 'Extracting Exhibits',
      weight: 20,
    },
    {
      task_type: 'DEPOSITION_EVENTS',
      task_name: 'Extracting Key Events',
      weight: 20,
    },
    {
      task_type: 'DEPOSITION_CONTENT',
      task_name: 'Drafting Summary',
      weight: 20,
    },
    {
      task_type: 'DEPOSITION_HTML',
      task_name: 'Finalizing Document',
      weight: 5,
    },
  ],
  // Mirror of use_case/progress_steps.py (backend). Generating Report is
  // last: the report is written and rendered after the exhibits are verified.
  use_case: [
    {
      task_type: 'USE_CASE_TEXT_EXTRACTION',
      task_name: 'Extracting Text',
      weight: 30,
    },
    {
      task_type: 'USE_CASE_GENERATION',
      task_name: 'Drafting Content',
      weight: 45,
    },
    {
      task_type: 'USE_CASE_EXHIBITS',
      task_name: 'Finalizing Exhibits',
      weight: 15,
    },
    {
      task_type: 'USE_CASE_REPORT',
      task_name: 'Generating Report',
      weight: 10,
    },
  ],
};

export const normalizeKey = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

export const getTaskDefinitions = (pipeline?: Pipeline) =>
  TASK_DEFINITIONS_BY_PIPELINE[pipeline || 'demand_letter'];

export const getTaskDefinition = (
  taskType?: string,
  taskName?: string,
  pipeline?: Pipeline
) => {
  const key = normalizeKey(taskType || taskName || '');
  return getTaskDefinitions(pipeline).find(
    (task) =>
      normalizeKey(task.task_type) === key ||
      normalizeKey(task.task_name) === key
  );
};

interface OrderableTask {
  task_type: string;
  task_name: string;
  step_index?: number;
}

/**
 * Order tasks the way the pipeline runs: by the position the backend sent
 * with the event when there is one, else by this file's list. Tasks known to
 * neither keep their arrival order after the known steps.
 */
export const orderTasksForPipeline = <T extends OrderableTask>(
  tasks: readonly T[],
  pipeline?: Pipeline
): T[] => {
  const order = getTaskDefinitions(pipeline).map((task) =>
    normalizeKey(task.task_type)
  );
  const rank = (task: T) => {
    if (typeof task.step_index === 'number') return task.step_index;
    const index = order.indexOf(normalizeKey(task.task_type || task.task_name));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return tasks
    .map((task, arrival) => ({ task, arrival, rank: rank(task) }))
    .sort((a, b) => a.rank - b.rank || a.arrival - b.arrival)
    .map(({ task }) => task);
};

// Expected total seconds per pipeline, used only until the first progress
// event brings the server's own estimate. ZLAYT-1579: every pipeline used to
// assume 20 minutes, so a report at 26% read "About 15 minutes" while
// production reports finished in 8 to 94 seconds and deposition summaries in
// under a minute. The demand letter keeps its old figure; the backend sends a
// measured estimate for it (ZLAYT-1448).
export const TOTAL_ESTIMATED_SECONDS_BY_PIPELINE: Record<Pipeline, number> = {
  demand_letter: 1200,
  deposition_summary: 120,
  use_case: 120,
};

// Expected seconds for one step, used before the first progress event.
export const getExpectedDurationSeconds = (
  taskType?: string,
  taskName?: string,
  pipeline?: Pipeline
) => {
  const definitions = getTaskDefinitions(pipeline);
  const definition = getTaskDefinition(taskType, taskName, pipeline);
  if (!definition) return 180;
  const total =
    TOTAL_ESTIMATED_SECONDS_BY_PIPELINE[pipeline || 'demand_letter'];
  const totalWeight = definitions.reduce((sum, task) => sum + task.weight, 0);
  // A floor keeps a light step from reading as instant; it scales with the
  // pipeline so the short pipelines do not add up past their total.
  const floor = Math.min(60, Math.round(total * 0.05));
  return Math.max(floor, Math.round((definition.weight / totalWeight) * total));
};
